import { useEffect, useState } from 'react';
import { SellingOwnerEmploymentAgreementRow, TermForm } from '../../../../../slices/marketplace/loi-form/term-form';
import { DataTable, DataTableView } from '../../../../../models/DataRequestHub/DataTable';
import { Timeline } from '../../../../../slices/marketplace/timeline/models/timeline.model';
import { TimelineActionType } from '../../../../../slices/marketplace/timeline/models/timeline-action-type.model';

const OWNER_DVM_POSITION = 'Owner / DVM';
const POSITION_COLUMN_KEYWORD = 'position';

export interface OwnerEmploymentAgreementDurationInitResult {
    hasOwnerEmploymentAgreement: boolean;
    columns: string[];
    rowDisplayData: Record<string, Record<string, string>>;
    tableRows: Array<{ tableRowId: number; customRowId: string }>;
}

interface UseOwnerEmploymentAgreementDurationInitProps {
    engagementId: number;
    termForm: TermForm | undefined | null;
    onChange: (termForm: TermForm) => void;
    axios: any;
}

function findTableAndViewByViewId(
    tables: DataTable[],
    viewId: number,
): { dataTable: DataTable; dataView: DataTableView } | null {
    for (const t of tables) {
        const v = (t.views ?? []).find(
            (v) => v.id === viewId || v.originalTableViewId === viewId
        );
        if (v) return { dataTable: t, dataView: v };
    }
    return null;
}

export function useOwnerEmploymentAgreementDurationInit({
    engagementId,
    termForm,
    onChange,
    axios,
}: UseOwnerEmploymentAgreementDurationInitProps): OwnerEmploymentAgreementDurationInitResult {
    const [hasOwnerEmploymentAgreement, setHasOwnerEmploymentAgreement] = useState(false);
    const [columns, setColumns] = useState<string[]>([]);
    const [rowDisplayData, setRowDisplayData] = useState<Record<string, Record<string, string>>>({});
    const [tableRows, setTableRows] = useState<Array<{ tableRowId: number; customRowId: string }>>([]);
    const [visibleRowIds, setVisibleRowIds] = useState<Set<number> | null>(null);

    useEffect(() => {
        if (!visibleRowIds || !termForm) return;
        const currentRows = termForm.sellingOwnerEmploymentAgreementRows;
        if (!currentRows || currentRows.length === 0) return;
        const syncedRows = currentRows.filter((r) => visibleRowIds.has(r.tableRowId));
        if (syncedRows.length !== currentRows.length) {
            onChange({ ...termForm, sellingOwnerEmploymentAgreementRows: syncedRows });
        }
    }, [visibleRowIds, termForm, onChange]);

    useEffect(() => {
        if (!axios || !engagementId) return;

        let cancelled = false;

        (async () => {
            try {
                const engagementResponse = await axios.get(`/api/Marketplace/Engagements/${engagementId}`);
                if (cancelled || engagementResponse.status !== 200) return;

                const timeline: Timeline = engagementResponse.data;
                const engagementData = timeline.engagementData;

                const loiEvent = timeline.events?.find(
                    (e) => e.linkedAction?.actionType === TimelineActionType.BuyerLoiSubmission
                );
                const loiDoc = loiEvent?.linkedAction?.referenceItemId
                    ? engagementData.documentsList?.find(
                          (d) => d.id === loiEvent.linkedAction!.referenceItemId
                      )
                    : null;

                if (!loiDoc?.linkedProjectId || !loiDoc?.dataTableViewId) {
                    setHasOwnerEmploymentAgreement(false);
                    return;
                }

                const linkedProject = engagementData.dataRequestProjects.find(
                    (p) => p.id === loiDoc.linkedProjectId,
                );

                if (!linkedProject?.dataRequestProjectId) {
                    setHasOwnerEmploymentAgreement(false);
                    return;
                }

                setHasOwnerEmploymentAgreement(true);

                try {
                    const projectResponse = await axios.get(
                        `/api/DataRequestProjects/${linkedProject.dataRequestProjectId}`,
                    );
                    if (cancelled || projectResponse.status !== 200) return;

                    const tables: DataTable[] = projectResponse.data.dataTables ?? [];

                    const found = findTableAndViewByViewId(tables, loiDoc.dataTableViewId);
                    if (!found) return;
                    const { dataTable, dataView } = found;

                    const includedColumnIds = new Set(
                        (dataView.viewColumns ?? []).filter((vc) => vc.isIncluded).map((vc) => vc.customColumnId)
                    );
                    const includedColumns = dataTable.columns.filter((c) => includedColumnIds.has(c.customColumnId));
                    const columnNames = includedColumns.map((c) => c.name);
                    setColumns(columnNames);

                    const columnIdToName = new Map(includedColumns.map((c) => [c.customColumnId, c.name]));

                    const positionColumn = includedColumns.find(
                        (c) => c.name.toLowerCase().includes(POSITION_COLUMN_KEYWORD)
                    );

                    const allVisibleRows = (dataTable.rows ?? []).filter((r) => r.isVisible);

                    const visibleRows = positionColumn
                        ? allVisibleRows.filter((row) => {
                              const positionCell = (row.cells ?? []).find(
                                  (cell) => cell.customColumnId === positionColumn.customColumnId
                              );
                              return positionCell?.answerText?.toLowerCase() === OWNER_DVM_POSITION.toLowerCase();
                          })
                        : allVisibleRows;

                    const displayData: Record<string, Record<string, string>> = {};
                    for (const row of visibleRows) {
                        const cells: Record<string, string> = {};
                        for (const cell of row.cells ?? []) {
                            const colName = columnIdToName.get(cell.customColumnId);
                            if (colName) cells[colName] = cell.answerText ?? '';
                        }
                        displayData[row.customRowId] = cells;
                    }
                    setRowDisplayData(displayData);
                    setTableRows(visibleRows.map((r) => ({ tableRowId: r.id, customRowId: r.customRowId })));
                    setVisibleRowIds(new Set(visibleRows.map((r) => r.id)));
                } catch (err) {
                    console.error('[OwnerEmploymentAgreement] Failed to fetch DR Hub data:', err);
                }
            } catch (err) {
                console.error('[OwnerEmploymentAgreement] Failed to load engagement data:', err);
                setHasOwnerEmploymentAgreement(false);
            }
        })();

        return () => { cancelled = true; };
    }, [axios, engagementId]);

    return { hasOwnerEmploymentAgreement, columns, rowDisplayData, tableRows };
}
